import { format, subDays } from 'date-fns';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import ICON_REFUND from '@/assets/cs/icon_refund.png';
import { Notification } from '@/types/cs.type';
import { comma } from '@/util/format';
import { readAlert } from '@/api/cs';
import { useMutation } from 'react-query';
import { ALERT_RENDER_MAP } from '@/constants/cs';
import ICON_PACKAGE from '@/assets/cs/icon_package.png';

type Props = {
  result: Notification;
  onOpenBottomSheet: (info: any) => void;
};

function CsResultItem({ result, onOpenBottomSheet }: Props) {
  const { mutate: mutateReadResult } = useMutation(readAlert);

  const onReadResult = ({
    id,
    notificationType,
  }: {
    id: number;
    notificationType: 'ONE_ON_ONE' | 'REQUIREMENT_RESULT';
  }) => {
    mutateReadResult(
      { chatId: id, notificationType },
      {
        onSuccess: () => {
          onOpenBottomSheet(result.message);
        },
      },
    );
  };

  const getTime = (dateTime: string) => {
    if (
      format(new Date(dateTime), 'yyyy-MM-dd') ===
      format(new Date(), 'yyyy-MM-dd')
    ) {
      return format(new Date(dateTime), 'HH:mm');
    }

    if (
      format(new Date(dateTime), 'yyyy-MM-dd') ===
      format(subDays(new Date(), 1), 'yyyy-MM-dd')
    ) {
      return '어제';
    }

    return format(new Date(dateTime), 'MM.dd');
  };

  const requestInfo = useMemo(() => {
    if (result.message.notificationType === 'REQUIREMENT_RESULT') {
      return JSON.parse(result.message.message);
    }
    return null;
  }, [result]);

  return (
    <ResultItem
      onClick={() =>
        onReadResult({
          id: result.chatId,
          notificationType: result.message.notificationType as
            | 'ONE_ON_ONE'
            | 'REQUIREMENT_RESULT',
        })
      }
    >
      <Header>
        <Image isPackage={requestInfo.type === 'PACKAGE_ACCEPTED'}>
          <img
            src={
              requestInfo.type === 'PACKAGE_ACCEPTED'
                ? ICON_PACKAGE
                : ICON_REFUND
            }
            alt=''
            width={24}
          />
        </Image>
        <Title read={result.read}>요청</Title>
        <Time>{getTime(result.createdAt)}</Time>
      </Header>
      <Content>
        <RequestInfo>
          {requestInfo?.type.includes('REFUND') ? (
            <RequestInfoList>
              <RequestInfoItem>
                <RequestInfoTitle>고객명</RequestInfoTitle>
                <RequestInfoData>
                  {JSON.parse(result.message.message).touristName}
                </RequestInfoData>
              </RequestInfoItem>
              <RequestInfoItem>
                <RequestInfoTitle>시술금액</RequestInfoTitle>
                <RequestInfoData>
                  {comma(JSON.parse(result.message.message).productAmount)}원
                </RequestInfoData>
              </RequestInfoItem>
            </RequestInfoList>
          ) : requestInfo?.type === 'PACKAGE_ACCEPTED' ? (
            <PackageInfoWrapper>
              {requestInfo?.packageNames.map((packageName: string) => (
                <RequestInfoList isPackage>
                  <RequestInfoTitle>{packageName}</RequestInfoTitle>
                </RequestInfoList>
              ))}
              {requestInfo?.etc && (
                <RequestInfoList isPackage>
                  <RequestInfoTitle isEtcPackage={requestInfo?.etc}>
                    기타
                  </RequestInfoTitle>
                  <RequestInfoData>{requestInfo.etc}</RequestInfoData>
                </RequestInfoList>
              )}
            </PackageInfoWrapper>
          ) : null}
        </RequestInfo>
        <ResultContent>
          {requestInfo && ALERT_RENDER_MAP[requestInfo.type]}
        </ResultContent>
      </Content>
    </ResultItem>
  );
}

const ResultItem = styled.button`
  padding: 14px 16px 5px 16px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  margin-bottom: 6px;
`;

const Image = styled.div<{ isPackage: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 100px;
  background: ${({ isPackage }) =>
    isPackage
      ? `radial-gradient(
    85.38% 103.93% at 60.71% 16.07%,
    #6fa0ff 0%,
    rgba(36, 108, 246, 0.775) 65.27%,
    rgba(36, 108, 246, 0.12) 100%
  )`
      : '#E5E5E5'};
`;

const Title = styled.strong<{ read: boolean }>`
  position: relative;
  color: #3a3b3e;
  font-size: 16px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: -0.32px;

  ${({ read }) =>
    !read &&
    css`
      &:after {
        content: '';
        position: absolute;
        top: 0;
        right: -8px;
        width: 5px;
        height: 5px;
        background-color: #ff2d55;
        border-radius: 100px;
      }
    `}
`;

const Time = styled.span`
  color: #5f6165;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.28px;
  margin-left: auto;
`;

const ResultContent = styled.p`
  text-align: left;
  color: #3a3b3e;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
`;

const RequestInfoList = styled.dl<{ isPackage?: boolean }>`
  color: #80848a;
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  letter-spacing: -2%;
  background-color: #f5f6f7;
  width: 100%;
  height: 24px;
  border-radius: 6px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  margin-bottom: 4px;

  ${({ isPackage }) =>
    isPackage &&
    css`
      display: inline-flex;
      width: auto;
      flex-shrink: 0;
    `}
`;

const RequestInfoItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;

  &:first-child {
    position: relative;
    &:after {
      content: '';
      width: 1px;
      height: 12px;
      border-radius: 100px;
      background-color: #cbccce;
      position: absolute;
      top: 50%;
      right: -8px;
      transform: translateY(-50%);
    }
  }
`;

const RequestInfoTitle = styled.dt<{ isEtcPackage?: boolean }>`
  position: relative;
  font-weight: 700;
  flex-shrink: 0;

  ${({ isEtcPackage }) =>
    isEtcPackage &&
    css`
      &:before {
        content: '';
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: -8px;
        width: 1px;
        height: 12px;
        background-color: #a7a7a7;
      }
    `}
`;

const RequestInfoData = styled.dd`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: left;
`;

const Content = styled.div`
  margin-left: 35px;
`;

const RequestInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  width: 251px;
  overflow: hidden;
`;

const PackageInfoWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export default CsResultItem;
